<div class="sf-wrap">

  <div class="sf-step-row">
    <span class="sf-circle">{{ 2 + stepOffset }}</span>
    <div>
      <span class="sf-step-title">STEP {{ 2 + stepOffset }} - Seasons and fields</span>
      <p class="sf-step-description">
        Add a season, then pick the fields in it. A field can belong to one season on a
        recommendation, so once it is chosen the other seasons show it as taken.
      </p>
    </div>
  </div>

  <div class="sf-loading" *ngIf="loading">
    <mat-spinner diameter="26"></mat-spinner>
    <span>Loading farms and seasons...</span>
  </div>

  <ng-container *ngIf="!loading">

    <!-- Add a season -->
    <div class="sf-add-row">
      <mat-form-field appearance="outline" class="sf-add-select">
        <mat-label>Add a season</mat-label>
        <mat-select [value]="null" (selectionChange)="addSeason($any($event).value)">
          <mat-option *ngFor="let s of addableSeasons" [value]="s.id">
            {{ s.name }} <span class="sf-muted">({{ s.available }} fields)</span>
          </mat-option>
        </mat-select>
      </mat-form-field>
      <span class="sf-add-hint" *ngIf="blocks.length === 0 && allSeasons.length">
        Start by adding the season this recommendation is for.
      </span>
      <span class="sf-add-hint" *ngIf="allSeasons.length === 0">
        None of this client's fields are linked to a season yet.
      </span>
    </div>

    <!-- One block per chosen season -->
    <div class="sf-season" *ngFor="let block of blocks; trackBy: trackSeason">

      <div class="sf-season-head">
        <mat-icon class="sf-season-icon">event</mat-icon>
        <span class="sf-season-name">{{ block.name }}</span>
        <span class="sf-season-count">{{ selectedCount(block) }} of {{ block.available }} fields</span>
        <span class="sf-spacer"></span>
        <button mat-icon-button (click)="removeSeason(block)" [attr.aria-label]="'Remove ' + block.name">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <div class="sf-farm" *ngFor="let farm of block.farms">
        <label class="sf-farm-head">
          <mat-checkbox [checked]="allSelected(block, farm)" [indeterminate]="someSelected(block, farm)"
                        (change)="toggleFarm(block, farm)"></mat-checkbox>
          <mat-icon class="sf-farm-icon">agriculture</mat-icon>
          <span class="sf-farm-name">{{ farm.farmName }}</span>
        </label>

        <!--
          A grid rather than one checkbox per row. The old screen ran a single column down the left
          of a full width panel, which is the wasted space this is meant to fix. auto-fill means it
          reflows to whatever width the page gives it instead of a fixed column count.
        -->
        <div class="sf-field-grid">
          <div class="sf-field" *ngFor="let field of farm.fields; trackBy: trackField"
               [class.sf-field--selected]="field.selected"
               [class.sf-field--taken]="claimedElsewhere(block, field)">

            <label class="sf-field-main">
              <mat-checkbox [checked]="field.selected" [disabled]="claimedElsewhere(block, field)"
                            (change)="toggleField(block, field)"></mat-checkbox>
              <span class="sf-field-text">
                <span class="sf-field-name">{{ field.fieldName }}</span>

                <span class="sf-field-sub" *ngIf="claimedElsewhere(block, field)">
                  Already in {{ claimedByName(field) }}
                </span>

                <span class="sf-field-sub" *ngIf="!claimedElsewhere(block, field) && field.definitions.length === 1">
                  {{ field.definitions[0].cultivar || 'No cultivar' }}
                  <ng-container *ngIf="field.definitions[0].cultivarSize != null">
                    · {{ field.definitions[0].cultivarSize | number:'1.2-2' }} ha
                  </ng-container>
                </span>

                <span class="sf-field-sub" *ngIf="!claimedElsewhere(block, field) && isMultiCultivar(field)">
                  {{ field.definitions.length }} cultivars
                </span>

                <span class="sf-field-sub sf-field-sub--none"
                      *ngIf="!claimedElsewhere(block, field) && field.definitions.length === 0">
                  No crop definition
                </span>
              </span>
            </label>

            <!--
              Only where the field has more than one cultivar AND has been chosen. Shown in place
              rather than in a later step, because narrowing a field is part of choosing it.
            -->
            <div class="sf-cultivars" *ngIf="field.selected && isMultiCultivar(field)">
              <label class="sf-cultivar" *ngFor="let def of field.definitions">
                <mat-checkbox [checked]="isDefinitionSelected(field, def.id)"
                              (change)="toggleDefinition(field, def.id)"></mat-checkbox>
                <span>{{ def.cultivar || 'No cultivar' }}</span>
                <span class="sf-muted" *ngIf="def.cultivarSize != null">
                  {{ def.cultivarSize | number:'1.2-2' }} ha
                </span>
              </label>

              <p class="sf-warn" *ngIf="hasNothingChosen(field)">
                <mat-icon>warning_amber</mat-icon>
                No cultivar chosen, this field will be left out
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!--
      Said out loud instead of appearing as an empty dropdown. On the existing screen these can be
      ticked and are then sent with no season at all.
    -->
    <div class="sf-orphans" *ngIf="orphanFields.length">
      <mat-icon class="sf-orphan-icon">info</mat-icon>
      <div class="sf-orphan-body">
        <div class="sf-orphan-top">
          <span>
            <strong>{{ orphanFields.length }} fields are not linked to any season</strong>
            and cannot be added to a recommendation. Link them in Season Manager first.
          </span>
          <button mat-button class="sf-orphan-toggle" (click)="showOrphans = !showOrphans">
            {{ showOrphans ? 'Hide' : 'Show' }} the list
            <mat-icon>{{ showOrphans ? 'expand_less' : 'expand_more' }}</mat-icon>
          </button>
        </div>

        <!--
          Grouped by farm, because that is how somebody goes and fixes them. Collapsed by default:
          it is a footnote on this screen, not the job.
        -->
        <div class="sf-orphan-list" *ngIf="showOrphans">
          <div class="sf-orphan-farm" *ngFor="let farm of orphanFarms">
            <span class="sf-orphan-farm-name">{{ farm.farmName }}</span>
            <span class="sf-orphan-field" *ngFor="let name of farm.fields">{{ name }}</span>
          </div>
        </div>
      </div>
    </div>

  </ng-container>
</div>
